import React, { useEffect, useState } from 'react';
import PageHero from './PageHero';
import '../styles/pages/company.css';

/**
 * Long-form legal layout: light PageHero + sticky table of contents (desktop)
 * + readable prose column.
 *
 * <CompanyLegal
 *   slug="privacy"
 *   title={<>Privacy <em>policy</em></>}
 *   meta={[{ label: 'Effective', value: 'March 13, 2026' }]}
 *   intro={<p>…</p>}                           optional, above the first section
 *   sections={[{ id: 'scope', num: '1', title: 'Scope and purpose', body: <>…</> }]}  (define at module level)
 * />
 */
export default function CompanyLegal({ slug, crumb, title, lead, meta = [], intro, sections }) {
  const [active, setActive] = useState(sections[0] && sections[0].id);

  // Highlight the section currently being read (progressive enhancement only).
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return undefined;
    const els = sections.map((s) => document.getElementById(`${slug}-${s.id}`)).filter(Boolean);
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id.slice(slug.length + 1));
      },
      { rootMargin: '-20% 0px -65% 0px', threshold: 0 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [slug, sections]);

  return (
    <>
      <PageHero
        id={`${slug}-hero`}
        className="co-hero co-legal-hero"
        eyebrow="Legal & trust"
        title={title}
        lead={lead}
        crumbs={[{ to: '/', label: 'Home' }, { label: crumb }]}
      >
        <dl className="co-legal-meta">
          <div><dt>Organization</dt><dd>Moneli Automation Inc.</dd></div>
          {meta.map((m) => (
            <div key={m.label}><dt>{m.label}</dt><dd>{m.value}</dd></div>
          ))}
        </dl>
      </PageHero>

      <section className="section section--sand co-legal" aria-label={typeof crumb === 'string' ? crumb : undefined}>
        <div className="container">
          <div className="co-legal__grid">
            <nav className="co-toc" aria-labelledby={`${slug}-toc-title`}>
              <p className="co-toc__title" id={`${slug}-toc-title`}>On this page</p>
              <ol>
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${slug}-${s.id}`}
                      className={active === s.id ? 'is-active' : undefined}
                      aria-current={active === s.id ? 'true' : undefined}
                    >
                      {s.num && <span className="co-toc__num" aria-hidden="true">{s.num}</span>}
                      <span>{s.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            <article className="co-prose">
              {intro && <div className="co-prose__intro">{intro}</div>}
              {sections.map((s) => (
                <section key={s.id} id={`${slug}-${s.id}`} className="co-prose__section" aria-labelledby={`${slug}-${s.id}-h`}>
                  <h2 id={`${slug}-${s.id}-h`}>
                    {s.num && <span className="co-prose__num">{s.num}<span className="visually-hidden">)</span></span>}
                    {s.title}
                  </h2>
                  {s.body}
                </section>
              ))}
            </article>
          </div>
        </div>
      </section>
    </>
  );
}
